import React from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import useWorkout from '../hooks/useWorkout'
import { estimateMinutes, formatClock } from '../utils/workout'
import Seo from '../components/Seo'
import { site, breadcrumbSchema } from '../seo'

// One line describing what a client actually has to do for this item.
const describeItem = (item) => {
  const sets = Math.max(1, parseInt(item.sets, 10) || 1)
  const effort = item.mode === 'timed'
    ? formatClock(parseInt(item.workSeconds, 10) || 0)
    : `${item.reps} reps`
  return `${sets} × ${effort}`
}

function WorkoutDetail() {
  const { id } = useParams()
  const { workout, exercisesById, loading, error, legacyExerciseId } = useWorkout(id)

  // Old /workouts/:id links pointed at a single exercise.
  if (legacyExerciseId) {
    return <Navigate to={`/exercises/${legacyExerciseId}`} replace />
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32 flex justify-center">
        <div className="text-xl">Loading workout...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <Link to="/workouts" className="underline mt-2 inline-block">
            Back to all workouts
          </Link>
        </div>
      </div>
    )
  }

  const totalSets = workout.items.reduce(
    (total, item) => total + Math.max(1, parseInt(item.sets, 10) || 1),
    0
  )

  return (
    <div className="container mx-auto px-4 py-16 pt-32">
      <div className="max-w-4xl mx-auto">
        <Seo
          title={`${workout.title} | Free Guided Workout | ${site.siteName}`}
          description={
            workout.description
              ? workout.description.replace(/\s+/g, ' ').trim().slice(0, 155)
              : `${workout.title}: a free guided workout of ${workout.items.length} exercises, about ${estimateMinutes(workout)} minutes, from Surrey personal trainer Alex Albert.`
          }
          type="article"
          schema={breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Workouts', path: '/workouts' },
            { name: workout.title, path: `/workouts/${workout.id}` }
          ])}
        />
        <Link to="/workouts" className="text-indigo-600 hover:text-indigo-800 mb-6 inline-block">
          &larr; Back to all workouts
        </Link>

        <h1 className="text-4xl font-bold mb-4">{workout.title}</h1>

        <div className="flex flex-wrap gap-2 mb-6">
          <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
            {workout.difficulty}
          </span>
          <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
            about {estimateMinutes(workout)} min
          </span>
          <span className="bg-orange-100 text-orange-800 text-sm px-3 py-1 rounded-full">
            {workout.items.length} exercises &middot; {totalSets} sets
          </span>
          {workout.category && (
            <span className="bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full">
              {workout.category}
            </span>
          )}
        </div>

        {workout.description && (
          <p className="text-gray-700 whitespace-pre-line mb-8">{workout.description}</p>
        )}

        {workout.items.length > 0 && (
          <Link
            to={`/workouts/${workout.id}/play`}
            className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-10 rounded-lg transition duration-200 mb-10 text-lg"
          >
            Start workout
          </Link>
        )}

        <h2 className="text-2xl font-semibold mb-4">What you'll do</h2>
        <ol className="space-y-3 mb-12">
          {workout.items.map((item, index) => {
            const exercise = exercisesById[item.exerciseId]
            return (
              <li
                key={`${item.exerciseId}-${index}`}
                className="border border-gray-200 rounded-lg p-4 flex items-start gap-4"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    {exercise ? (
                      <Link
                        to={`/exercises/${exercise.id}`}
                        className="font-bold text-indigo-700 hover:text-indigo-900"
                      >
                        {exercise.title}
                      </Link>
                    ) : (
                      // The exercise was deleted from the library after the
                      // workout was built — show the name we stored with it.
                      <span className="font-bold text-gray-700">
                        {item.exerciseTitle || 'Exercise'}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-600">
                      {describeItem(item)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {item.restSeconds > 0
                      ? `${formatClock(item.restSeconds)} rest between sets`
                      : 'No rest between sets'}
                  </p>
                  {item.notes && (
                    <p className="text-sm text-gray-600 mt-2 italic">{item.notes}</p>
                  )}
                </div>
              </li>
            )
          })}
        </ol>

        {/* Call to Action Banner */}
        <div className="bg-indigo-100 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-indigo-800 mb-2">
            Want personalised guidance?
          </h3>
          <p className="text-indigo-700 mb-4">
            Book a consultation with our expert trainers to get a customised workout plan tailored to your specific needs and goals.
          </p>
          <Link
            to="/book-consultation"
            className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
          >
            Book a Consultation
          </Link>
        </div>
      </div>
    </div>
  )
}

export default WorkoutDetail
